import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import type { User } from '@voll-intelligence/domain';

import { authRepository, meRepository } from '@web/data/repositories';
import type { OrgEntry } from '@web/data/repositories';
import { queryKeys } from '@web/core/query';
import { useOrgContext } from '@web/ui/core/hooks';
import { fireAndForget } from '@web/ui/core/utils';

export type PresenceStatus = 'online' | 'busy' | 'away' | 'offline';

export interface ProfileMenuState {
  user: User | null;
  signOut: () => Promise<void>;
  /** Active presence status. Local-only — no `/me/status` endpoint yet. */
  status: PresenceStatus;
  setStatus: (s: PresenceStatus) => void;
  /** All orgs the caller belongs to, in `/me` order. */
  orgs: OrgEntry[];
  /** The currently-active org (the one whose slug is in the URL). */
  activeOrg: OrgEntry | null;
  goToProfile: () => void;
  goToMembers: () => void;
  switchOrg: (slug: string) => void;
}

export function useProfileMenu(): ProfileMenuState {
  const navigate = useNavigate();
  const { orgSlug } = useOrgContext();
  const [status, setStatus] = useState<PresenceStatus>('online');

  const { data: me } = useQuery({
    queryKey: queryKeys.me.all,
    queryFn: () => meRepository.getMe(),
  });
  const user: User | null = me ? { id: me.id as User['id'], name: me.name, email: me.email } : null;
  const orgs = me?.orgs ?? [];
  const activeOrg = orgs.find((o) => o.slug === orgSlug) ?? null;

  const signOut = useCallback(async () => {
    await authRepository.signOut();
    await navigate({ to: '/login' });
  }, [navigate]);

  const goToProfile = useCallback(() => {
    fireAndForget(navigate({ to: '/orgs/$orgSlug/settings/profile', params: { orgSlug } }));
  }, [navigate, orgSlug]);

  const goToMembers = useCallback(() => {
    fireAndForget(navigate({ to: '/orgs/$orgSlug/settings/members', params: { orgSlug } }));
  }, [navigate, orgSlug]);

  const switchOrg = useCallback(
    (slug: string) => {
      fireAndForget(navigate({ to: '/orgs/$orgSlug/overview', params: { orgSlug: slug } }));
    },
    [navigate],
  );

  return {
    user,
    signOut,
    status,
    setStatus,
    orgs,
    activeOrg,
    goToProfile,
    goToMembers,
    switchOrg,
  };
}
